import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  ChevronRight,
  Calendar,
  Hash,
  Link2,
  Sparkles,
  TrendingUp,
  Target,
  Building,
  Edit3,
  Eye,
  Plus,
  RefreshCw,
  Search,
  X,
  Copy,
  BookOpen,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Pin,
  PinOff
} from 'lucide-react';
import { format, differenceInDays, isAfter } from 'date-fns';
import { toast } from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';
import EssaySlotService from '../../../services/EssaySlotService';
import EssaySlotCard from './EssaySlotCard';
import ContentAssignmentModal from './ContentAssignmentModal';

// Reuse Suggestions Modal Component
const ReuseSuggestionsModal = ({ slotOverview, userId, onClose }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSuggestions();
  }, []);

  const loadSuggestions = async () => {
    try {
      setLoading(true);
      // Analyze slots to find reuse opportunities
      const slotsWithEssays = slotOverview?.colleges?.flatMap(college => 
        college.slots?.filter(slot => slot.assigned_essay_id)
      ) || [];
      
      const emptySlots = slotOverview?.colleges?.flatMap(college => 
        college.slots?.filter(slot => !slot.assigned_essay_id)
      ) || [];

      // Find similar prompts or essay types that could be reused
      const reuseSuggestions = [];
      
      slotsWithEssays.forEach(filledSlot => {
        emptySlots.forEach(emptySlot => {
          // Check for similar slot types
          if (filledSlot.slot_type === emptySlot.slot_type) {
            reuseSuggestions.push({
              fromSlot: filledSlot,
              toSlot: emptySlot,
              matchType: 'Same Essay Type',
              confidence: 'High'
            });
          }
          // Check for similar prompts (simplified - in real app would use more sophisticated matching)
          else if (filledSlot.prompt && emptySlot.prompt && 
                   filledSlot.prompt.toLowerCase().includes('why') && 
                   emptySlot.prompt.toLowerCase().includes('why')) {
            reuseSuggestions.push({
              fromSlot: filledSlot,
              toSlot: emptySlot,
              matchType: 'Similar Topic',
              confidence: 'Medium'
            });
          }
        });
      });

      setSuggestions(reuseSuggestions.slice(0, 10)); // Limit to top 10 suggestions
    } catch (error) {
      console.error('Error loading suggestions:', error);
      toast.error('Failed to load reuse suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleReuse = async (suggestion) => {
    try {
      // In a real implementation, this would copy/link the essay
      await EssaySlotService.assignEssayToSlot(
        suggestion.toSlot.id, 
        suggestion.fromSlot.assigned_essay_id
      );
      toast.success('Essay successfully reused!');
      onClose();
    } catch (error) {
      console.error('Error reusing essay:', error);
      toast.error('Failed to reuse essay');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Essay Reuse Suggestions</h2>
              <p className="text-slate-600 mt-1">
                Smart recommendations for essays that could work for multiple slots
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-stone-500" />
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : suggestions.length > 0 ? (
            <div className="space-y-4">
              {suggestions.map((suggestion, index) => (
                <div key={index} className="bg-stone-50 rounded-lg p-4 hover:bg-stone-100 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                          suggestion.confidence === 'High' 
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {suggestion.confidence} Match
                        </span>
                        <span className="text-sm text-slate-600">{suggestion.matchType}</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-stone-500 mb-1">From:</p>
                          <p className="font-medium text-slate-900">
                            {suggestion.fromSlot.essay_title || 'Untitled Essay'}
                          </p>
                          <p className="text-sm text-slate-600 mt-1">
                            {suggestion.fromSlot.college_name}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-xs text-stone-500 mb-1">To:</p>
                          <p className="font-medium text-slate-900">
                            {suggestion.toSlot.slot_type?.replace(/_/g, ' ')}
                          </p>
                          <p className="text-sm text-slate-600 mt-1">
                            {suggestion.toSlot.college_name}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleReuse(suggestion)}
                      className="ml-4 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Reuse
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-slate-600">No reuse opportunities found</p>
              <p className="text-sm text-stone-500 mt-1">
                Complete more essays to discover reuse opportunities
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EssaySlotDashboard = ({ userId, onSlotClick, onAssignContent, showReuseSuggestions: initialShowReuse = false, initialSelectedSlotId = null }) => {
  const [slotOverview, setSlotOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCollege, setFilterCollege] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [showReuseSuggestions, setShowReuseSuggestions] = useState(initialShowReuse);
  const [expandedSlotId, setExpandedSlotId] = useState(initialSelectedSlotId); // Auto-expand the selected slot
  const [assignmentModal, setAssignmentModal] = useState({
    isOpen: false,
    slotId: null,
    contentType: null
  });
  const [collapsedColleges, setCollapsedColleges] = useState(new Set());
  const [pinnedColleges, setPinnedColleges] = useState(new Set());

  // Handle initial show reuse suggestions
  useEffect(() => {
    if (initialShowReuse) {
      setShowReuseSuggestions(true);
    }
  }, [initialShowReuse]);

  // Load slot overview
  const loadSlotOverview = useCallback(async () => {
    try {
      setLoading(true);
      const overview = await EssaySlotService.getUserSlotOverview(userId);
      console.log('Loaded slot overview:', overview); // Debug log
      setSlotOverview(overview);

      // Set all colleges to collapsed by default (excluding pinned ones)
      if (overview?.colleges) {
        const collegeIds = overview.colleges.map(c => c.college_id);
        const nonPinnedIds = collegeIds.filter(id => !pinnedColleges.has(id));
        setCollapsedColleges(new Set(nonPinnedIds));
      }
    } catch (error) {
      console.error('Error loading slot overview:', error);
      toast.error('Failed to load essay slots');
    } finally {
      setLoading(false);
    }
  }, [userId, pinnedColleges]);

  useEffect(() => {
    if (userId) {
      loadSlotOverview();
    }
  }, [userId, loadSlotOverview]);

  // Auto-expand college and trigger slot click when initialSelectedSlotId is provided
  useEffect(() => {
    if (initialSelectedSlotId && slotOverview?.colleges) {
      // Find which college contains this slot
      for (const college of slotOverview.colleges) {
        const slot = college.slots?.find(s => s.id === initialSelectedSlotId);
        if (slot) {
          // Expand this college
          setCollapsedColleges(prev => {
            const updated = new Set(prev);
            updated.delete(college.college_id);
            return updated;
          });

          // Trigger slot click to open it
          if (onSlotClick) {
            setTimeout(() => {
              onSlotClick(slot);
              // Scroll to the slot element after a brief delay
              setTimeout(() => {
                const slotElement = document.getElementById(`slot-${initialSelectedSlotId}`);
                if (slotElement) {
                  slotElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 300);
            }, 100);
          }
          break;
        }
      }
    }
  }, [initialSelectedSlotId, slotOverview, onSlotClick]);

  // Calculate progress metrics
  const calculateProgress = () => {
    if (!slotOverview) return { percentage: 0, label: 'No slots' };
    
    const total = slotOverview.totalSlots;
    const completed = slotOverview.completedSlots;
    const inProgress = slotOverview.inProgressSlots;
    
    if (total === 0) return { percentage: 0, label: 'No essays required' };
    
    const percentage = Math.round((completed / total) * 100);
    const remaining = total - completed;
    
    return {
      percentage,
      label: `${completed} of ${total} complete`,
      remaining,
      inProgress
    };
  };

  const progress = calculateProgress();

  // Toggle college section collapse
  const toggleCollegeCollapse = (collegeId) => {
    setCollapsedColleges(prev => {
      const newSet = new Set(prev);
      if (newSet.has(collegeId)) {
        newSet.delete(collegeId);
      } else {
        newSet.add(collegeId);
      }
      return newSet;
    });
  };

  // Toggle college pin (max 2 pinned colleges)
  const toggleCollegePin = (collegeId, event) => {
    event.stopPropagation(); // Prevent collapse toggle when clicking pin

    setPinnedColleges(prev => {
      const newSet = new Set(prev);

      if (newSet.has(collegeId)) {
        // Unpinning
        newSet.delete(collegeId);
        // Add to collapsed when unpinned
        setCollapsedColleges(curr => new Set([...curr, collegeId]));
        toast.success('College unpinned');
      } else {
        // Pinning
        if (newSet.size >= 2) {
          toast.error('You can only pin up to 2 colleges');
          return prev;
        }
        newSet.add(collegeId);
        // Remove from collapsed when pinned
        setCollapsedColleges(curr => {
          const updated = new Set(curr);
          updated.delete(collegeId);
          return updated;
        });
        toast.success('College pinned to top');
      }

      return newSet;
    });
  };

  // Filter slots based on search and filters
  const getFilteredColleges = () => {
    if (!slotOverview) return [];

    let colleges = [...slotOverview.colleges];

    // Apply college filter
    if (filterCollege !== 'all') {
      colleges = colleges.filter(c => c.college_id === filterCollege);
    }

    // Apply status filter to slots
    if (filterStatus !== 'all') {
      colleges = colleges.map(college => ({
        ...college,
        slots: college.slots.filter(slot => {
          if (filterStatus === 'empty') return slot.status === 'empty';
          if (filterStatus === 'in_progress') return ['brainstorming', 'drafting', 'revising', 'review'].includes(slot.status);
          if (filterStatus === 'complete') return slot.status === 'complete';
          return true;
        })
      })).filter(c => c.slots.length > 0);
    }

    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      colleges = colleges.map(college => ({
        ...college,
        slots: college.slots.filter(slot =>
          slot.prompt_text?.toLowerCase().includes(term) ||
          slot.slot_type?.toLowerCase().includes(term) ||
          college.college_name?.toLowerCase().includes(term)
        )
      })).filter(c => c.slots.length > 0);
    }

    // Sort: pinned colleges first, then the rest
    colleges.sort((a, b) => {
      const aIsPinned = pinnedColleges.has(a.college_id);
      const bIsPinned = pinnedColleges.has(b.college_id);

      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;

      // If both pinned or both unpinned, maintain original order
      return 0;
    });

    return colleges;
  };

  const filteredColleges = getFilteredColleges();

  // Get status icon and color
  const getStatusDisplay = (status) => {
    const displays = {
      empty: { icon: Circle, color: 'text-stone-400', bg: 'bg-stone-50' },
      brainstorming: { icon: Sparkles, color: 'text-violet-500', bg: 'bg-violet-50' },
      drafting: { icon: Edit3, color: 'text-azure-500', bg: 'bg-azure-50' },
      revising: { icon: RefreshCw, color: 'text-yellow-500', bg: 'bg-yellow-50' },
      review: { icon: Eye, color: 'text-orange-500', bg: 'bg-orange-50' },
      complete: { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-50' }
    };
    return displays[status] || displays.empty;
  };

  // Calculate days until deadline
  const getDaysUntil = (deadline) => {
    if (!deadline) return null;
    const days = differenceInDays(new Date(deadline), new Date());
    if (days < 0) return { days: Math.abs(days), overdue: true };
    return { days, overdue: false };
  };

  // Get deadline urgency color
  const getDeadlineColor = (days) => {
    if (!days) return 'text-stone-500';
    if (days.overdue) return 'text-red-600 font-semibold';
    if (days.days <= 7) return 'text-red-500';
    if (days.days <= 14) return 'text-orange-500';
    if (days.days <= 30) return 'text-yellow-600';
    return 'text-slate-600';
  };

  // Handle slot selection for bulk actions
  const toggleSlotSelection = (slotId) => {
    setSelectedSlots(prev => 
      prev.includes(slotId) 
        ? prev.filter(id => id !== slotId)
        : [...prev, slotId]
    );
  };

  // Find reusable slots
  const handleFindReusableSlots = async (slotId) => {
    try {
      const suggestions = await EssaySlotService.findReusableSlots(slotId, userId);
      if (suggestions.length > 0) {
        toast.success(`Found ${suggestions.length} similar slots that could reuse this essay`);
        // Could open a modal here to show suggestions
      } else {
        toast.info('No similar slots found for reuse');
      }
    } catch (error) {
      console.error('Error finding reusable slots:', error);
      toast.error('Failed to find reusable slots');
    }
  };

  // Handle opening assignment modal
  const handleOpenAssignmentModal = (contentType, slotId) => {
    setAssignmentModal({
      isOpen: true,
      slotId,
      contentType
    });
  };

  // Handle content assignment from modal
  const handleAssignContentFromModal = async (slotId, contentType, contentId) => {
    try {
      await EssaySlotService.assignContent(slotId, contentType, contentId);
      toast.success('Content assigned successfully');
      
      // Refresh the data without closing modal immediately
      await loadSlotOverview();
      
      // Close modal after a brief delay so user sees the success
      setTimeout(() => {
        setAssignmentModal({ isOpen: false, slotId: null, contentType: null });
      }, 500);
    } catch (error) {
      console.error('Error assigning content:', error);
      toast.error('Failed to assign content');
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azure-600"></div>
      </div>
    );
  }

  // Calculate circle progress for sidebar
  const circleRadius = 50;
  const circleCircumference = 2 * Math.PI * circleRadius;
  const circleProgress = (progress.percentage / 100) * circleCircumference;

  return (
    <div className="min-h-screen bg-stone-50 p-4 sm:p-6">
      {/* Show Reuse Suggestions Modal if triggered */}
      {showReuseSuggestions && (
        <ReuseSuggestionsModal
          slotOverview={slotOverview}
          userId={userId}
          onClose={() => setShowReuseSuggestions(false)}
        />
      )}

      {/* Main Content - Full width since progress is in main sidebar */}
          {/* Header */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Essay Slots</h1>
              <p className="text-slate-600 mt-1 text-sm sm:text-base">
                Organize and track all your essay requirements in one place
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className="px-3 sm:px-4 py-2 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
                onClick={() => setShowReuseSuggestions(true)}
              >
                View Reuse Suggestions
              </button>
              <button
                className="px-3 sm:px-4 py-2 bg-stone-100 hover:bg-stone-200 text-slate-700 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
                onClick={loadSlotOverview}
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

      {/* Overall Progress Banner */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-4 sm:p-6 text-white mb-6 shadow-lg">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-4">
          <div className="text-center">
            <div className="text-4xl font-bold mb-1">{slotOverview?.totalSlots || 0}</div>
            <div className="text-sm opacity-90">Total Essays</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold mb-1">{slotOverview?.completedSlots || 0}</div>
            <div className="text-sm opacity-90">Complete</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold mb-1">{progress.inProgress || 0}</div>
            <div className="text-sm opacity-90">In Progress</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold mb-1">{progress.remaining || 0}</div>
            <div className="text-sm opacity-90">Not Started</div>
          </div>
        </div>
        <div className="w-full bg-white bg-opacity-20 rounded-full h-2 overflow-hidden">
          <div 
            className="h-full bg-white rounded-full transition-all duration-700 ease-out"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>
        {progress.percentage > 0 && (
          <p className="text-sm mt-2 opacity-90">
            {progress.percentage}% complete • Keep up the great work!
          </p>
        )}
      </div>

      {/* Search and Filters */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="w-5 h-5 absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              placeholder="Search essays by prompt, college, or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-white border border-stone-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all text-sm sm:text-base"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2.5 sm:py-3 bg-white border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 flex-shrink-0"
          >
            <option value="all">All Status</option>
            <option value="empty">Not Started</option>
            <option value="in_progress">In Progress</option>
            <option value="complete">Complete</option>
          </select>
        </div>
      </div>

      {/* Personal Statement */}
      {slotOverview?.personalStatement && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
            Personal Statement
            <span className="text-sm font-normal bg-violet-100 text-violet-700 px-3 py-1 rounded-full">
              Common App • Used by {slotOverview.colleges.length} colleges
            </span>
          </h2>
          
          <EssaySlotCard
            slot={slotOverview.personalStatement}
            college={null}
            deadline={null}
            onSlotUpdate={loadSlotOverview}
            onSlotClick={onSlotClick}
            onAssignContent={(contentType, slotId) => {
              handleOpenAssignmentModal(contentType, slotId);
            }}
            onRemoveContent={(slotId, contentId, contentType) => {
              EssaySlotService.removeContentFromSlot(slotId, contentType, contentId)
                .then(() => {
                  toast.success('Content removed');
                  loadSlotOverview();
                })
                .catch(error => {
                  console.error('Error removing content:', error);
                  toast.error('Failed to remove content');
                });
            }}
          />
        </div>
      )}

      {/* College Slots */}
      <div className="space-y-6">
        {filteredColleges.length === 0 ? (
          <div className="text-center py-12 bg-stone-50 rounded-lg">
            <FileText className="w-12 h-12 text-stone-400 mx-auto mb-3" />
            <p className="text-slate-600">No essay slots found</p>
            <p className="text-sm text-stone-500 mt-1">
              Add colleges to see their essay requirements
            </p>
          </div>
        ) : (
          filteredColleges.map(college => {
            const isCollapsed = collapsedColleges.has(college.college_id);
            const completedCount = college.slots.filter(s => s.status === 'complete').length;
            const withDraftsCount = college.slots.filter(s => s.assigned_essay_id).length;
            
            const isPinned = pinnedColleges.has(college.college_id);

            return (
              <div key={college.college_id} className={`bg-white rounded-xl border overflow-hidden shadow-sm ${
                isPinned ? 'border-violet-300 ring-2 ring-violet-100' : 'border-stone-200'
              }`}>
                {/* Collapsible Header */}
                <div className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-white border-b border-stone-200 relative">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <button
                      onClick={() => {
                        console.log('Toggle college collapse clicked:', college.college_id);
                        toggleCollegeCollapse(college.college_id);
                      }}
                      className="flex items-center gap-2 sm:gap-3 flex-1 hover:opacity-80 transition-opacity text-left min-w-0"
                    >
                      <ChevronRight
                        className={`w-4 h-4 sm:w-5 sm:h-5 text-stone-400 transition-transform flex-shrink-0 ${
                          !isCollapsed ? 'rotate-90' : ''
                        }`}
                      />
                      <Building className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600 flex-shrink-0" />
                      <h3 className="font-semibold text-slate-900 text-base sm:text-lg truncate">{college.college_name}</h3>
                      {isPinned && (
                        <span className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded text-xs font-medium pointer-events-none flex-shrink-0">
                          PINNED
                        </span>
                      )}
                      <span className="bg-stone-100 text-slate-700 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm pointer-events-none flex-shrink-0">
                        {college.slots.length} essays
                      </span>
                    </button>

                    <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 text-xs sm:text-sm pl-6 sm:pl-0">
                      {withDraftsCount > 0 && (
                        <span className="text-orange-600 font-medium">
                          {withDraftsCount} with drafts
                        </span>
                      )}
                      {completedCount > 0 && (
                        <span className="text-emerald-600 font-medium">
                          {completedCount} complete
                        </span>
                      )}
                      {withDraftsCount === 0 && completedCount === 0 && (
                        <span className="text-stone-400">
                          No drafts yet
                        </span>
                      )}

                      {/* Pin Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCollegePin(college.college_id, e);
                        }}
                        className={`p-2 rounded-lg transition-all hover:scale-110 ${
                          isPinned
                            ? 'text-violet-600 hover:bg-violet-50'
                            : 'text-stone-400 hover:bg-stone-100 hover:text-stone-600'
                        }`}
                        title={isPinned ? 'Unpin college' : 'Pin college (max 2)'}
                      >
                        {isPinned ? (
                          <Pin className="w-4 h-4 fill-current" />
                        ) : (
                          <PinOff className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>


                {/* Expandable Content */}
                {!isCollapsed && (
                  <div className="p-4 sm:p-6 bg-stone-50">
                    <div className="space-y-3 sm:space-y-4">
                      {college.slots.map(slot => (
                        <EssaySlotCard
                          key={slot.id}
                          slot={slot}
                          college={college}
                          deadline={college.deadlines?.ed || college.deadlines?.ea || college.deadlines?.rd}
                          onSlotUpdate={loadSlotOverview}
                          onSlotClick={onSlotClick}
                          onAssignContent={(contentType, slotId) => {
                            handleOpenAssignmentModal(contentType, slotId);
                          }}
                          onRemoveContent={(slotId, contentId, contentType) => {
                            EssaySlotService.removeContentFromSlot(slotId, contentType, contentId)
                              .then(() => {
                                toast.success('Content removed');
                                loadSlotOverview();
                              })
                              .catch(error => {
                                console.error('Error removing content:', error);
                                toast.error('Failed to remove content');
                              });
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

          {/* Content Assignment Modal */}
          <ContentAssignmentModal
            isOpen={assignmentModal.isOpen}
            onClose={() => setAssignmentModal({ isOpen: false, slotId: null, contentType: null })}
            userId={userId}
            slotId={assignmentModal.slotId}
            contentType={assignmentModal.contentType}
            onAssign={handleAssignContentFromModal}
          />

          {/* Floating Action Button */}
          <button
            onClick={() => {
              // Navigate to essay creation or open modal
              console.log('Create new essay');
            }}
            className="fixed bottom-8 right-8 w-14 h-14 bg-violet-600 hover:bg-violet-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
            title="Create New Essay"
          >
            <Plus className="w-6 h-6 group-hover:scale-110 transition-transform" />
          </button>
        </div>
  );
};

export default EssaySlotDashboard;